import { Cloud, Brain, Server, Cpu, Box } from 'lucide-react';
import { useInView } from '../hooks/useInView';
import type { ReactNode } from 'react';

interface Provider {
  name: string;
  icon: ReactNode;
  description: string;
}

const providers: Provider[] = [
  {
    name: 'OpenAI',
    icon: <Brain className="h-8 w-8" aria-hidden="true" />,
    description: 'GPT-4, GPT-3.5, and embedding models',
  },
  {
    name: 'Anthropic',
    icon: <Cpu className="h-8 w-8" aria-hidden="true" />,
    description: 'Claude family of models',
  },
  {
    name: 'Azure OpenAI',
    icon: <Cloud className="h-8 w-8" aria-hidden="true" />,
    description: 'Enterprise OpenAI deployments',
  },
  {
    name: 'Google Vertex AI',
    icon: <Server className="h-8 w-8" aria-hidden="true" />,
    description: 'Gemini and PaLM models',
  },
  {
    name: 'AWS Bedrock',
    icon: <Box className="h-8 w-8" aria-hidden="true" />,
    description: 'Multi-model managed service',
  },
];

export function ProvidersSection() {
  const [headingRef, headingInView] = useInView();

  return (
    <section
      id="providers"
      className="relative py-24 sm:py-32 bg-gray-950"
      aria-labelledby="providers-heading"
    >
      <div
        className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent"
        aria-hidden="true"
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          ref={headingRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headingInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            id="providers-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
          >
            Connect any <span className="gradient-text">AI provider</span>
          </h2>
          <p className="mt-4 text-lg text-gray-400">
            Opair supports all major AI providers out of the box. Route requests across providers
            with intelligent fallback, cost optimization, and compliance-aware selection.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
          {providers.map((provider, i) => (
            <ProviderCard key={provider.name} provider={provider} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProviderCard({ provider, index }: { provider: Provider; index: number }) {
  const [ref, isInView] = useInView();

  return (
    <div
      ref={ref}
      className={`group flex flex-col items-center text-center rounded-2xl border border-gray-800 bg-gray-900/50 p-6 hover:border-indigo-500/50 hover:bg-gray-900/80 transition-all duration-700 ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="mb-3 inline-flex items-center justify-center rounded-lg bg-indigo-500/10 p-3 text-indigo-400 group-hover:bg-indigo-500/20 transition-colors">
        {provider.icon}
      </div>
      <h3 className="text-base font-semibold text-white">{provider.name}</h3>
      <p className="mt-1 text-xs text-gray-500">{provider.description}</p>
    </div>
  );
}
